/**
 * Authentication utility functions
 */
import { decodeJwtPayload, persistProductName } from "./product";

// Store a reference server timestamp on page load
const PAGE_LOAD_TIME = Date.now();
const TIME_CHECK_INTERVAL = 30000; // 30 seconds

// Store last validated time to detect time jumps
let lastCheckedTime = Date.now();

/**
 * Detects if system time has been manipulated
 * @returns {boolean} True if time has been changed significantly
 */
const hasTimeChanged = () => {
  const now = Date.now();
  const expectedElapsed = now - PAGE_LOAD_TIME;

  // Calculate how much time should have passed since the last check
  const expectedSinceLastCheck = now - lastCheckedTime;

  // If time jumped backward or jumped too far forward (more than 2x expected interval)
  const hasTimeJumped =
    expectedSinceLastCheck < 0 ||
    expectedSinceLastCheck > TIME_CHECK_INTERVAL * 2;

  // Update the last checked time
  lastCheckedTime = now;

  if (hasTimeJumped) {
    console.warn("System time appears to have changed:", {
      now: new Date(now).toISOString(),
      expectedElapsed,
      timeSinceLastCheck: expectedSinceLastCheck,
      threshold: TIME_CHECK_INTERVAL * 2,
    });
  }

  return hasTimeJumped;
};

/**
 * Checks if the session is valid and not expired
 * @returns {boolean} True if session is valid
 */
export const isSessionValid = () => {
  // Check if system time has been changed
  if (hasTimeChanged()) {
    console.warn("System time changed detected - forcing fresh validation");
    // We won't immediately invalidate, but will do a fresh check
  }

  // First check sessionStorage
  const sessionSid = sessionStorage.getItem("verificationSid");
  const sessionToken = sessionStorage.getItem("authToken");

  // Check localStorage for session data
  const sessionDataStr = localStorage.getItem("sessionData");

  // If there's localStorage data, ALWAYS check expiration first
  if (sessionDataStr) {
    try {
      const sessionData = JSON.parse(sessionDataStr);
      const expiresAt = new Date(sessionData.expiresAt);
      const now = new Date();

      // Check if the session has expired
      if (expiresAt <= now) {
        console.warn("[isSessionValid] Session expired");
        clearAuthStorage();
        return false;
      }

      // Extra validation: If user changed time backward to avoid expiration,
      // check if session is older than the maximum allowed duration
      const sessionCreatedAt = new Date(sessionData.createdAt || 0);
      const maxSessionDuration = 24 * 60 * 60 * 1000; // 24 hours in ms
      const sessionAge = now - sessionCreatedAt;

      if (sessionCreatedAt.getTime() > 0 && sessionAge > maxSessionDuration) {
        console.warn(
          "Session exceeds maximum duration - possible time manipulation",
        );
        clearAuthStorage();
        return false;
      }

      // Session is not expired, still valid
      if (sessionData.verificationSid) {
        // Restore to sessionStorage for app use
        sessionStorage.setItem("verificationSid", sessionData.verificationSid);

        const userEmail = localStorage.getItem("userEmail");
        const phoneNumber = localStorage.getItem("phoneNumber");
        const authToken = localStorage.getItem("authToken");
        const onboarding = localStorage.getItem("onboarding");

        if (userEmail) sessionStorage.setItem("userEmail", userEmail);
        if (phoneNumber) sessionStorage.setItem("phoneNumber", phoneNumber);
        if (onboarding) sessionStorage.setItem("onboarding", onboarding);
        if (authToken) sessionStorage.setItem("authToken", authToken);

        // Valid if we have a JWT, OR an email-verified session. Email-only
        // login returns no JWT yet (the backend JWT piece is still pending),
        // so accept a verified-email session on its own for now.
        const emailVerified = sessionData.emailVerified === true;
        if (emailVerified) sessionStorage.setItem("emailVerified", "true");
        const isValid = !!authToken || emailVerified;
        return isValid;
      }
    } catch (error) {
      console.error("Error checking session validity:", error);
      clearAuthStorage();
      return false;
    }
  }

  // If no localStorage data but sessionStorage has verificationSid, accept a
  // JWT OR an email-verified session (email-only login has no JWT yet).
  const sessionEmailVerified =
    sessionStorage.getItem("emailVerified") === "true";
  if (sessionSid && (sessionToken || sessionEmailVerified)) {
    return true;
  }
  return false;
};

/**
 * Get the onboarding status from storage
 * @returns {boolean} The onboarding status
 */
export const getOnboardingStatus = () => {
  const onboarding =
    sessionStorage.getItem("onboarding") || localStorage.getItem("onboarding");
  return onboarding === "true";
};

/**
 * Clears all authentication storage (both sessionStorage and localStorage)
 */
export const clearAuthStorage = () => {
  // Clear sessionStorage
  sessionStorage.clear();

  // Clear localStorage items related to authentication
  localStorage.removeItem("sessionData");
  localStorage.removeItem("verificationSid");
  localStorage.removeItem("userEmail");
  localStorage.removeItem("phoneNumber");
  localStorage.removeItem("authToken");
  localStorage.removeItem("onboarding");
  localStorage.removeItem("lexiProduct");
  localStorage.removeItem("lexiFreeUser");
};

/**
 * Store authentication data with expiration
 * @param {string} verificationSid - Verification SID
 * @param {string} email - User email
 * @param {string} phoneNumber - User phone number
 * @param {string} token - JWT token (optional)
 * @param {number} expirationHours - Hours until expiration (default: 72)
 * @param {boolean} onboarding - Onboarding status (optional)
 */
export const storeAuthData = (
  verificationSid,
  email,
  phoneNumber,
  token = null,
  expirationHours = 72,
  onboarding = false,
) => {
  if (!verificationSid) {
    console.error("Cannot store auth data: verificationSid is required");
    return;
  }

  // Use the account value returned by verification, never a stale login/browser value.
  if (token) {
    try {
      const payload = decodeJwtPayload(token);
      if (payload && Object.prototype.hasOwnProperty.call(payload, "phone")) {
        phoneNumber = payload.phone || null;
      }
    } catch {
      // Existing callers without a decodable token retain their explicit phone argument.
    }
  }
  if (!phoneNumber) {
    sessionStorage.removeItem("phoneNumber");
    sessionStorage.removeItem("cleanPhoneNumber");
    localStorage.removeItem("phoneNumber");
  }

  // Store in sessionStorage for immediate use
  sessionStorage.setItem("verificationSid", verificationSid);
  if (email) sessionStorage.setItem("userEmail", email);
  if (phoneNumber) sessionStorage.setItem("phoneNumber", phoneNumber);
  if (token) {
    sessionStorage.setItem("authToken", token);

    // Extract and persist product name and onboarding from JWT token
    try {
      const payload = decodeJwtPayload(token);

      // Extract onboarding from token if available, otherwise use parameter
      const tokenOnboarding = payload?.onboarding;
      const finalOnboarding =
        tokenOnboarding !== undefined ? tokenOnboarding : onboarding;
      sessionStorage.setItem("onboarding", String(finalOnboarding));

      const subscription = payload?.subscription;
      const oneTimePayments = payload?.oneTimePayments;

      // Check subscriptions first
      if (
        subscription &&
        Array.isArray(subscription) &&
        subscription.length > 0
      ) {
        const activeSubscription =
          subscription.find(
            (sub) => sub.status === "active" || sub.status === "trialing",
          ) || subscription[0];

        const productName =
          activeSubscription?.items?.[0]?.price?.product?.name;
        if (productName) {
          persistProductName(productName);
        } else {
          persistProductName("Lexi Social");
        }
      }
      // Check one-time payments (e.g., 7-Day Pass)
      else if (
        oneTimePayments &&
        Array.isArray(oneTimePayments) &&
        oneTimePayments.length > 0
      ) {
        // Use the most recent one-time payment (create a copy to avoid mutating the original)
        const mostRecent = [...oneTimePayments].sort(
          (a, b) => b.created - a.created,
        )[0];
        const productName = mostRecent?.productName;
        if (productName) {
          persistProductName(productName);
        } else {
          console.warn(
            "[storeAuthData] One-time payment found but no productName, defaulting to Lexi Social",
          );
          persistProductName("Lexi Social");
        }
      }
      // No subscriptions or one-time payments
      else {
        persistProductName("Lexi Social");
      }
    } catch (error) {
      console.warn("Could not extract product name from token:", error);
      // If token extraction fails, still store the onboarding parameter
      sessionStorage.setItem("onboarding", String(onboarding));
    }
  } else {
    // No token provided, use the onboarding parameter
    sessionStorage.setItem("onboarding", String(onboarding));
  }

  // Store in localStorage with expiration for persistence
  const sessionData = {
    verificationSid,
    expiresAt: new Date(
      Date.now() + expirationHours * 60 * 60 * 1000,
    ).toISOString(),
    createdAt: new Date().toISOString(), // Add creation time to track session age
  };

  // Preserve email-only verification state when there's no JWT, so re-saving
  // the session (e.g. from ProtectedRoute) doesn't drop it and bounce the user.
  if (!token && sessionStorage.getItem("emailVerified") === "true") {
    sessionData.emailVerified = true;
  }

  // Get the final onboarding value from sessionStorage (which was set above)
  const finalOnboarding = sessionStorage.getItem("onboarding");

  localStorage.setItem("sessionData", JSON.stringify(sessionData));
  localStorage.setItem("verificationSid", verificationSid);
  if (email) localStorage.setItem("userEmail", email);
  if (phoneNumber) localStorage.setItem("phoneNumber", phoneNumber);
  if (finalOnboarding !== null)
    localStorage.setItem("onboarding", finalOnboarding);
  if (token) {
    localStorage.setItem("authToken", token);
  }
};

/**
 * FOR TESTING ONLY: Force session expiration
 * This can be called from browser console: window.expireSession()
 */
export const expireSession = () => {
  try {
    const sessionDataStr = localStorage.getItem("sessionData");
    if (sessionDataStr) {
      const sessionData = JSON.parse(sessionDataStr);
      // Set expiration to 1 minute ago
      const expiredDate = new Date(Date.now() - 60000);
      sessionData.expiresAt = expiredDate.toISOString();

      // Update localStorage
      localStorage.setItem("sessionData", JSON.stringify(sessionData));

      return true;
    } else {
      console.error("No session data found to expire");
      return false;
    }
  } catch (error) {
    console.error("Error expiring session:", error);
    return false;
  }
};

/**
 * FOR TESTING ONLY: Force logout immediately
 * This can be called from browser console: window.forceLogout()
 */
export const forceLogout = () => {
  clearAuthStorage();
  window.location.href = "/login";
  return true;
};

/**
 * FOR TESTING ONLY: Simulate changing system time
 * This can be called from browser console to test time change detection
 */
export const simulateTimeChange = (hoursToAdd) => {
  lastCheckedTime = Date.now() - hoursToAdd * 60 * 60 * 1000;
  return true;
};

/**
 * Syncs authentication data from cookies to localStorage/sessionStorage
 * Used for cross-domain auth via subdomain cookies (e.g., auth.lexi.tips -> lexi.tips)
 * @returns {boolean} True if auth data was synced from cookies
 */
export const syncCookiesToStorage = () => {
  if (typeof document === "undefined") return false;

  // Parse cookies into an object
  const cookies = document.cookie.split("; ").reduce((acc, cookie) => {
    const [name, ...valueParts] = cookie.split("=");
    acc[name] = decodeURIComponent(valueParts.join("="));
    return acc;
  }, {});

  // Check if we have auth cookies to sync
  const authToken = cookies["authToken"];
  const verificationSid = cookies["verificationSid"];

  if (!authToken || !verificationSid) {
    return false;
  }

  // Check if we already have valid session data in storage
  const existingToken =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
  if (existingToken === authToken) {
    // Already synced, no need to do it again
    return false;
  }

  console.log(
    "[syncCookiesToStorage] Syncing auth data from cookies to storage",
  );

  // Sync to sessionStorage
  sessionStorage.setItem("authToken", authToken);
  sessionStorage.setItem("verificationSid", verificationSid);
  if (cookies["userEmail"])
    sessionStorage.setItem("userEmail", cookies["userEmail"]);
  if (cookies["onboarding"])
    sessionStorage.setItem("onboarding", cookies["onboarding"]);

  // Sync to localStorage
  localStorage.setItem("authToken", authToken);
  localStorage.setItem("verificationSid", verificationSid);
  if (cookies["userEmail"])
    localStorage.setItem("userEmail", cookies["userEmail"]);
  if (cookies["onboarding"])
    localStorage.setItem("onboarding", cookies["onboarding"]);
  if (cookies["lexiProduct"])
    localStorage.setItem("lexiProduct", cookies["lexiProduct"]);

  // Parse and store sessionData
  if (cookies["sessionData"]) {
    try {
      const sessionData = JSON.parse(cookies["sessionData"]);
      localStorage.setItem("sessionData", JSON.stringify(sessionData));
    } catch (e) {
      console.warn(
        "[syncCookiesToStorage] Failed to parse sessionData cookie:",
        e,
      );
    }
  }

  // Clear the auth cookies after syncing to prevent repeated syncs
  // The data is now in localStorage/sessionStorage which is the primary auth source
  const cookieDomain = window.location.hostname.includes("localhost")
    ? ""
    : "; Domain=.lexi.tips";
  const clearCookie = (name) => {
    document.cookie = `${name}=; Path=/; Max-Age=0${cookieDomain}`;
  };

  clearCookie("authToken");
  clearCookie("verificationSid");
  clearCookie("userEmail");
  clearCookie("onboarding");
  clearCookie("sessionData");
  clearCookie("lexiProduct");

  return true;
};

const isDev = import.meta.env.VITE_APP_WORKSPACE === "dev" || false;
// Make testing functions available globally - ONLY in development
if (typeof window !== "undefined" && isDev) {
  window.expireSession = expireSession;
  window.forceLogout = forceLogout;
  window.checkSession = isSessionValid;
  window.simulateTimeChange = simulateTimeChange;
} else if (typeof window !== "undefined" && !isDev) {
  // In production, define these as no-op functions that log a warning
  window.expireSession =
    window.forceLogout =
    window.checkSession =
    window.simulateTimeChange =
      () => {
        console.warn("Test functions are disabled in production");
        return false;
      };
}
